import { useEffect, useRef, useState } from 'react'
import { Send, Loader2, Sparkles, Trash2, CheckCircle2, XCircle } from 'lucide-react'
import toast from 'react-hot-toast'
import { cn } from '@/lib/utils'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

const PLACEHOLDER_POR_PASO = {
  recopilando: 'Pregúntame algo sobre este proyecto…',
  preparando: 'Ej: "sugiéreme el mapeo de esta hoja"',
  cuadrado: 'Ej: "por qué no cuadra" o "cuadra las hojas"',
  entregado: 'Ej: "inicia la carga"',
}

/**
 * EL COPILOTO — panel angosto y fijo que acompaña los 4 pasos del asistente, no solo
 * Recopilar. Es distinto de `ChatMigracion` (que arma el catálogo inicial): este responde
 * preguntas y propone acciones concretas del paso en el que el asesor esté parado — sugerir
 * un mapeo en Preparar, explicar por qué no cuadra en Cuadrar, iniciar la carga en Cargar.
 *
 * Nunca aplica un cambio real sin que el asesor lo resuelva primero. Esa propuesta pendiente
 * NUNCA es "el último mensaje del chat" — es un campo propio del proyecto, así que se muestra
 * como un banner fijo (siempre visible, sobrevive a un refresh) y bloquea "Limpiar
 * conversación" hasta que se resuelva: confirmar, elegir una opción, o cancelar.
 */
export default function CopilotoPanel({
  mensajes = [],
  propuestaPendiente,
  onEnviarMensaje,
  onLimpiar,
  ocupado,
  paso,
  hojaActivaId,
}) {
  const [texto, setTexto] = useState('')
  const bodyRef = useRef(null)

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight
  }, [mensajes.length])

  const enviar = () => {
    const t = texto.trim()
    if (!t || ocupado) return
    setTexto('')
    onEnviarMensaje?.(t, paso, hojaActivaId)
  }

  const elegirOpcion = (indice) => {
    if (ocupado) return
    onEnviarMensaje?.('', paso, hojaActivaId, indice)
  }

  const confirmar = () => {
    if (ocupado) return
    onEnviarMensaje?.('sí', paso, hojaActivaId)
  }

  const cancelar = () => {
    if (ocupado) return
    onEnviarMensaje?.('no', paso, hojaActivaId)
  }

  const limpiar = async () => {
    if (propuestaPendiente) {
      toast.error('Hay una propuesta sin resolver — confírmala, elige una opción o cancélala antes de limpiar.')
      return
    }
    await onLimpiar?.()
  }

  return (
    <Card className="flex h-full min-h-[28rem] flex-col">
      <div className="flex items-center justify-between border-b border-neutral-200 dark:border-neutral-800 px-3 py-2.5">
        <div className="flex items-center gap-1.5">
          <Sparkles size={14} className="text-primary" />
          <p className="text-body-sm font-semibold text-neutral-900 dark:text-neutral-50">Copiloto</p>
        </div>
        <Button variant="ghost" size="icon-sm" onClick={limpiar} title="Limpiar conversación">
          <Trash2 size={14} />
        </Button>
      </div>

      {propuestaPendiente && (
        <div className="border-b border-warning-200 dark:border-warning-800 bg-warning-50 dark:bg-warning/15 px-3 py-2.5">
          <p className="text-caption text-warning-900 dark:text-warning-300">{propuestaPendiente.respuesta}</p>
          {propuestaPendiente.opciones?.length > 0 ? (
            <div className="mt-2 flex flex-col gap-1.5">
              {propuestaPendiente.opciones.map((op, i) => (
                <Button key={i} variant="outline" size="sm" onClick={() => elegirOpcion(i)} disabled={ocupado} className="justify-start">
                  {op.etiqueta}
                </Button>
              ))}
              <Button variant="ghost" size="sm" onClick={cancelar} disabled={ocupado} className="justify-start text-neutral-500 dark:text-neutral-400">
                <XCircle size={13} className="mr-1.5" /> Cancelar
              </Button>
            </div>
          ) : (
            <div className="mt-2 flex gap-1.5">
              <Button size="sm" onClick={confirmar} disabled={ocupado}>
                <CheckCircle2 size={13} className="mr-1.5" /> Confirmar
              </Button>
              <Button variant="outline" size="sm" onClick={cancelar} disabled={ocupado}>
                <XCircle size={13} className="mr-1.5" /> Cancelar
              </Button>
            </div>
          )}
        </div>
      )}

      <div ref={bodyRef} className="flex-1 space-y-2.5 overflow-y-auto px-3 py-3">
        {mensajes.length === 0 ? (
          <p className="text-caption text-neutral-400 dark:text-neutral-500">
            Te acompaño en todo el proceso. Pregúntame lo que necesites sobre este paso.
          </p>
        ) : (
          mensajes.map((m, i) => (
            <div
              key={i}
              className={cn(
                'max-w-[92%] rounded-lg px-2.5 py-1.5 text-caption',
                m.rol === 'asesor' ? 'ml-auto bg-primary text-white' : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-50'
              )}
            >
              {m.texto}
            </div>
          ))
        )}
        {ocupado && (
          <div className="flex items-center gap-1.5 text-caption text-neutral-400 dark:text-neutral-500">
            <Loader2 size={12} className="animate-spin" /> pensando…
          </div>
        )}
      </div>

      <div className="flex items-center gap-1.5 border-t border-neutral-200 dark:border-neutral-800 p-2.5">
        <input
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), enviar())}
          placeholder={PLACEHOLDER_POR_PASO[paso] || 'Escribe aquí…'}
          disabled={ocupado}
          className="h-10 min-w-0 flex-1 rounded-md border border-line bg-card px-2.5 text-caption text-foreground placeholder:text-neutral-400 dark:placeholder:text-neutral-500 focus:border-primary focus:outline-none"
        />
        <Button size="icon-sm" onClick={enviar} disabled={ocupado || !texto.trim()}>
          <Send size={14} />
        </Button>
      </div>
    </Card>
  )
}
